import { User, UserRole } from '../types';
import { detectMobileOrMiniProgram, resolveSupplierRoute } from './supplierView';
import {
  APPROVAL_WORKBENCH_ROLES,
  BUYER_WORKBENCH_ROLES,
  PLATFORM_WORKBENCH_ROLES,
  SETTINGS_COMPANY_ROLES,
  YANXUAN_WORKBENCH_ROLES,
  resolveDefaultRouteByRole,
} from './workbenchView';

/**
 * 校验 returnTo 路径是否允许当前角色访问（防止供应商登录后回跳到 /platform 等无权限页）
 */
export function isReturnToAllowedForRole(path: string, role: string): boolean {
  if (path.startsWith('/yanxuan')) {
    return YANXUAN_WORKBENCH_ROLES.includes(role as UserRole);
  }
  if (path.startsWith('/platform')) {
    return PLATFORM_WORKBENCH_ROLES.includes(role as UserRole);
  }
  if (path.startsWith('/approval')) {
    return APPROVAL_WORKBENCH_ROLES.includes(role as UserRole);
  }
  if (path.startsWith('/buyer') || path.startsWith('/central-procurement')) {
    return BUYER_WORKBENCH_ROLES.includes(role as UserRole) || role === UserRole.CENTRAL_PURCHASER;
  }
  if (path.startsWith('/supplier')) {
    return role === UserRole.SUPPLIER;
  }
  if (path.startsWith('/settings/company')) {
    return SETTINGS_COMPANY_ROLES.includes(role as UserRole);
  }
  return true;
}

/**
 * 校验登录后回跳路径，仅允许站内相对路径，防止开放重定向。
 */
export function resolveSafeReturnTo(raw: string | null | undefined): string | null {
  const value = String(raw || '').trim();
  if (!value) return null;
  if (!value.startsWith('/') || value.startsWith('//')) return null;
  if (value.startsWith('/login')) return null;
  return value;
}

export function readLoginReturnToFromSearch(search: string): string | null {
  const params = new URLSearchParams(search || '');
  return resolveSafeReturnTo(params.get('returnTo'));
}

export function buildLoginPathWithReturnTo(
  returnTo: string,
  loginPath = '/login',
): string {
  const safe = resolveSafeReturnTo(returnTo);
  const base = loginPath || '/login';
  if (!safe) return base;
  return `${base}?returnTo=${encodeURIComponent(safe)}`;
}

export function resolvePostLoginPath(
  user: User,
  options?: {
    returnTo?: string | null;
    preferMobileLayout?: boolean;
    tenantAuditStatus?: string | null;
  },
): string {
  if (user.must_change_password) {
    return '/account/set-password';
  }

  const audit = String(options?.tenantAuditStatus || '').toUpperCase();
  if (audit === 'PENDING' || audit === 'RESUBMIT') {
    return '/login';
  }
  if (audit === 'REJECTED') {
    return '/login';
  }

  const safeReturn = resolveSafeReturnTo(options?.returnTo);
  if (safeReturn && isReturnToAllowedForRole(safeReturn, user.role)) {
    return safeReturn;
  }

  if (user.role === 'supplier') {
    return resolveSupplierRoute();
  }
  const mobile =
    options?.preferMobileLayout !== undefined
      ? options.preferMobileLayout
      : detectMobileOrMiniProgram();
  return resolveDefaultRouteByRole(user.role, mobile);
}

export type SupplierWorkbenchChoice = 'direct' | 'yanxuan';

/** 登录回跳已明确指向某一工作台时，不再弹选择 */
export function workbenchChoiceFromReturnTo(
  returnTo?: string | null,
): SupplierWorkbenchChoice | null {
  const path = resolveSafeReturnTo(returnTo);
  if (!path) return null;
  if (path.startsWith('/yanxuan')) return 'yanxuan';
  if (path.startsWith('/supplier')) return 'direct';
  return null;
}

export function resolveSupplierWorkbenchPath(
  choice: SupplierWorkbenchChoice,
): string {
  return choice === 'yanxuan' ? '/yanxuan' : resolveSupplierRoute();
}
